// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ChangePasswordActions from "@insite/content-library/Widgets/ChangePassword/ChangePasswordActions";
import ChangePasswordHeader from "@insite/content-library/Widgets/ChangePassword/ChangePasswordHeader";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden/Hidden";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PasswordRequirementValidator from "@pace/App/Components/Common/PasswordRequirements";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    validatePassword,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    pageTitle: getCurrentPage(state).fields.title,
});

export interface ChangePasswordStyles {
    actions: HiddenProps;
    passwordRequirementsGridItem: GridItemProps;
    passwordRequirementsGridContainer: GridContainerProps;
    passwordGridContainer: GridContainerProps;
    signOutWarningText: TypographyPresentationProps;
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    showPasswordsGridItem?: GridItemProps;
    showPasswordsCheckbox?: CheckboxPresentationProps;
    changePasswordLink?: LinkPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    // XNG-Change
    passwordGridItem?: GridItemProps;
    passwordGridItemContainer?: GridContainerProps;
    actionsWrapper?: InjectableCss;
}

export const changePasswordStyles: ChangePasswordStyles = {
    actions: {
        css: css`
            width: 100%;
        `,
    },
    passwordGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        max-width: 300px;
                    `,
                    css`
                        max-width: 300px;
                    `,
                    css`
                        max-width: 300px;
                    `,
                ])}
        `,
        gap: 10,
    },
    passwordRequirementsGridContainer: {
        gap: 2,
        css: css`
            margin-bottom: 10px;
        `,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    showPasswordsGridItem: {
        width: 12,
    },
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    signOutWarningText: {
        css: css`
            padding-bottom: 10px;
            display: block;
        `,
    },
    userInformationTitle: {
        variant: "h4",
        as: "h2",
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
};

const styles = changePasswordStyles;

const ChangePasswordView = ({ accountSettings, pageTitle, validatePassword }: Props) => {
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    useEffect(() => {
        setError(
            !!newPasswordErrorMessage || !newPassword || !confirmNewPassword || confirmNewPassword !== newPassword,
        );
    }, [newPassword, newPasswordErrorMessage, confirmNewPassword]);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        logOutUserAfterPasswordChange,
    } = accountSettings;

    const currentPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setPassword(event.currentTarget.value);
    };

    const newPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newPassword = event.currentTarget.value;

        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setNewPasswordErrorMessage(errorMessage);
            },
        });

        setNewPassword(newPassword);
    };

    const confirmNewPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setConfirmNewPassword(event.currentTarget.value);
    };

    const confirmNewPasswordError =
        showValidation && newPassword && confirmNewPassword && newPassword !== confirmNewPassword
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
            : "";

    // XNG-Change: PasswordRequirementValidator
    return (
        <form>
            <ChangePasswordHeader
                title={pageTitle}
                password={password}
                newPassword={newPassword}
                confirmNewPassword={confirmNewPassword}
                error={error}
                showValidation={showValidation}
                setShowValidation={setShowValidation}
            ></ChangePasswordHeader>
            {logOutUserAfterPasswordChange && (
                <Typography {...styles.signOutWarningText}>
                    {siteMessage("Account_Automatic_SignOut_Warning")}
                </Typography>
            )}
            <GridContainer {...styles.passwordGridContainer}>
                <GridItem {...styles.passwordGridItem}>
                    <GridContainer {...styles.passwordGridItemContainer}>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("Current Password")}
                                onInput={currentPasswordChangeHandler}
                                autoComplete="current-password"
                                placeholder={translate("Your Current Password")}
                            />
                        </GridItem>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("New Password")}
                                onInput={newPasswordChangeHandler}
                                autoComplete="new-password"
                                placeholder={translate("New Password")}
                            />
                        </GridItem>
                        <GridItem {...styles.userInformationGridItem}>
                            <TextField
                                type={showPasswords ? "text" : "password"}
                                label={translate("Confirm New Password")}
                                onInput={confirmNewPasswordChangeHandler}
                                error={confirmNewPasswordError}
                                autoComplete="new-password"
                                placeholder={translate("Confirm New Password")}
                            />
                        </GridItem>
                        <GridItem {...styles.showPasswordsGridItem}>
                            <Checkbox
                                onChange={() => {
                                    setShowPasswords(!showPasswords);
                                }}
                                checked={showPasswords}
                            >
                                {translate("Show Passwords")}
                            </Checkbox>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.passwordRequirementsGridItem}>
                    <Typography {...styles.userInformationTitle}>{translate("Password Requirements")}</Typography>
                    <PasswordRequirementValidator
                        newPassword={newPassword}
                        minLength={passwordMinimumLength}
                        checkDigits={passwordRequiresDigit}
                        checkLowerCase={passwordRequiresLowercase}
                        checkUpperCase={passwordRequiresUppercase}
                        checkNonAlphaNumeric={passwordRequiresSpecialCharacter}
                    />
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.actionsWrapper}>
                <ChangePasswordActions
                    password={password}
                    newPassword={newPassword}
                    confirmNewPassword={confirmNewPassword}
                    error={error}
                    showValidation={showValidation}
                    setShowValidation={setShowValidation}
                ></ChangePasswordActions>
            </StyledWrapper>
        </form>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ChangePasswordView),
    definition: {
        allowedContexts: ["ChangePasswordPage"],
        group: "Change Password",
    },
};

export default widgetModule;
